import { Dialog, Transition } from '@headlessui/react';
import { clx } from '@medusajs/ui';
import React, { Fragment } from 'react';

import { ModalProvider, useModal } from '@lib/context/modal-context';
import Text from '@modules/common/components/text';
import X from '@modules/common/icons/x';

type ModalProps = {
  isOpen: boolean;
  close: () => void;
  size?: 'small' | 'medium' | 'large';
  search?: boolean;
  children: React.ReactNode;
  panelClassName?: string;
  autoClose?: boolean;
  'data-testid'?: string;
};

const noop = () => {};

const Modal = ({
  isOpen,
  close,
  size = 'medium',
  search = false,
  panelClassName = '',
  autoClose = true,
  children,
  'data-testid': dataTestId,
}: ModalProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[75]"
        onClose={autoClose ? close : noop}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 h-screen bg-opacity-75 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-hidden">
          <div
            className={clx(
              'flex h-full min-h-full justify-center p-4 text-center',
              {
                'items-center': !search,
                'items-start': search,
              }
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid={dataTestId}
                className={clx(
                  'flex h-fit max-h-[75vh] w-full transform flex-col justify-start overflow-auto p-6 text-left align-middle transition-all',
                  {
                    'max-w-md': size === 'small',
                    'max-w-xl': size === 'medium',
                    'max-w-3xl': size === 'large',
                    'bg-transparent shadow-none': search,
                    'rounded-rounded border bg-white shadow-xl': !search,
                  },
                  panelClassName
                )}
              >
                <ModalProvider close={close}>{children}</ModalProvider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { close } = useModal();

  return (
    <Dialog.Title className="flex items-center justify-between">
      <Text as="div" variant="large-semi">{children}</Text>
      <div>
        <button onClick={close} data-testid="close-modal-button" aria-label="關閉">
          <X size={20} />
        </button>
      </div>
    </Dialog.Title>
  );
};

const Description: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Dialog.Description className="text-xs font-normal leading-5 text-ui-fg-base flex h-full items-center justify-center pb-4 pt-2">
      {children}
    </Dialog.Description>
  );
};

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex justify-center">{children}</div>;
};

const Footer: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div className={clx('flex items-center justify-end gap-x-4', className)}>
      {children}
    </div>
  );
};

Modal.Title = Title;
Modal.Description = Description;
Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
